import { Link, Badge, AppImage, FormattedDate } from '@shopmuchmore/design-system';
import { formatPrice } from '@shopmuchmore/common-utils';
import type { OrderSummary } from '../orders.types';

interface OrderCardProps {
    order: OrderSummary;
}

// Map order status to badge variant
function getStatusVariant(status: string): 'success' | 'warning' | 'default' | 'destructive' | 'secondary' {
    const s = status.toLowerCase();
    const statusMap: Record<string, 'success' | 'warning' | 'default' | 'destructive' | 'secondary'> = {
        'completed': 'success',
        'success': 'success',
        'pending': 'warning',
        'cancelled': 'destructive',
        'failed': 'destructive',
        'refunded': 'default',
    };
    return statusMap[s] || 'default';
}

export function OrderCard({ order }: OrderCardProps) {
    return (
        <div className="bg-white rounded-xl border-[1.5px] border-gray-100 p-4 md:p-6 flex flex-col md:flex-row gap-4 md:items-center transition-all hover:border-black/10 hover:shadow-sm">
            {/* Top section: Thumbnail + Order Info */}
            <div className="flex gap-4 items-start flex-1 min-w-0 flex-col lg:flex-row">
                {/* Product Thumbnail */}
                <AppImage src={order.order_image || undefined} alt={`Order ${order.order_number}`} size="md" className='w-full lg:w-auto' />

                <div className="flex-1 min-w-0 space-y-2">
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-bold text-gray-900 text-base md:text-lg">Order #{order.order_number}</span>
                        <Badge 
                            variant={getStatusVariant(order.status)} 
                            size="sm"
                            className={order.status.toLowerCase() === 'refunded' ? 'bg-purple-600 text-white border-transparent px-2.5 shadow-sm' : ''}
                        >
                            {(order.status || 'unknown').toUpperCase()}
                        </Badge>
                    </div>
                    <div className="text-sm text-gray-500">
                        Placed on <FormattedDate date={order.order_date} /> • {order.items} {order.items === 1 ? 'item' : 'items'}
                    </div>
                    <div className="font-bold text-gray-900">
                        Total: {formatPrice(order.total_amount)}
                    </div>
                </div>
            </div>

            {/* Button section */}
            <Link href={`/account/orders/${order.order_number}`} variant="outline"> View Details </Link>
        </div>
    );
}
